// External packages
import { useFormContext, useFieldArray } from 'react-hook-form';
import * as React from 'react';

// Components
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';
import { WorkingHoursField } from 'components/WorkingHours';
import { Option } from 'components/Select';
import { Button } from 'components/Button';
import { Select } from 'components/Select';
import { Label } from 'components/Forms';
import { Icon } from 'components/Icons';

type Day =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const AddWorkingHours: React.FC<{
  label?: string;
  className?: string;
}> = ({ label, className }) => {
  const days: { key: Day; label: string }[] = [
    { key: 'monday', label: 'Ponedjeljak' },
    { key: 'tuesday', label: 'Utorak' },
    { key: 'wednesday', label: 'Srijeda' },
    { key: 'thursday', label: 'Četvrtak' },
    { key: 'friday', label: 'Petak' },
    { key: 'saturday', label: 'Subota' },
    { key: 'sunday', label: 'Nedjelja' },
  ];

  const { control, watch, setValue } = useFormContext();
  const [copyOption, setCopyOption] = React.useState<Record<Day, string>>({
    monday: '',
    tuesday: '',
    wednesday: '',
    thursday: '',
    friday: '',
    saturday: '',
    sunday: '',
  });

  const fieldArrays = days.reduce(
    (acc, d) => {
      acc[d.key] = useFieldArray({
        control,
        name: `weeklyAvailability.${d.key}` as const,
      });
      return acc;
    },
    {} as Record<Day, ReturnType<typeof useFieldArray>>
  );

  const handleCopyHours = (fromDay: Day) => {
    const option = copyOption[fromDay];
    let targetDays: Day[] = [];
    if (option === 'all') {
      targetDays = days.map((d) => d.key);
    } else if (option === 'working') {
      targetDays = days
        .filter((d) =>
          ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(
            d.key
          )
        )
        .map((d) => d.key as Day);
    } else if (option === 'weekend') {
      targetDays = days
        .filter((d) => ['saturday', 'sunday'].includes(d.key))
        .map((d) => d.key as Day);
    }

    const slots = watch('weeklyAvailability') || {};
    const fromSlots = slots[fromDay]
      ? slots[fromDay].map((f: any) => ({
          startTime: f.startTime,
          endTime: f.endTime,
        }))
      : [];
    targetDays.forEach((d) => {
      setValue(`weeklyAvailability.${d}`, fromSlots);
    });
  };

  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <Tabs orientation="vertical">
        <TabList>
          {days.map((d) => (
            <Tab key={d.key} id={d.key}>
              {d.label}
            </Tab>
          ))}
        </TabList>
        {days.map((d) => (
          <TabPanel key={d.key} id={d.key}>
            <div className="flex flex-col gap-4">
              <div className="flex relative before:absolute before:-left-6 before:-bottom-2 before:content-[''] before:w-[calc(100%+1.5625rem)] before:h-0.5 before:bg-gray-300 w-full items-center gap-2 mb-3">
                <Select
                  selectedKey={copyOption[d.key]}
                  onSelectionChange={(val: any) =>
                    setCopyOption((prev) => ({
                      ...prev,
                      [d.key]: val as string,
                    }))
                  }
                  placeholder="Kopiraj na..."
                  className="w-40"
                >
                  <Option id="all">Sve dane</Option>
                  <Option id="working">Radne dane</Option>
                  <Option id="weekend">Vikend</Option>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onPress={() => handleCopyHours(d.key)}
                  isDisabled={!copyOption[d.key]}
                >
                  Kopiraj
                </Button>
              </div>
              {fieldArrays[d.key].fields.length === 0 && (
                <div className="text-gray-400 text-sm">Nema smjena</div>
              )}
              {fieldArrays[d.key].fields.map((field, idx) => {
                return (
                  <WorkingHoursField
                    key={field.id}
                    name={`weeklyAvailability.${d.key}.${idx}`}
                    showRemove={true}
                    onRemove={() => fieldArrays[d.key].remove(idx)}
                    className="mb-2"
                  />
                );
              })}
              <Button
                size="sm"
                icon={<Icon name="plus" />}
                className="self-start"
                onPress={() =>
                  fieldArrays[d.key].append({ startTime: '', endTime: '' })
                }
              >
                Dodaj smjenu
              </Button>
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};
